import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles()((theme) => ({
  content: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 5fr) minmax(0, 7fr)",
    gap: theme.spacing(2),
    height: "70vh",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
      height: "auto",
    },
  },
  tableBox: {
    overflowY: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    ...theme.scrollbarStyles,
  },
  row: {
    cursor: "pointer",
  },
  rowSelected: {
    background: theme.palette.action.selected,
  },
  note: {
    fontSize: 12.5,
    color: theme.palette.text.secondary,
  },
  diffBox: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    overflow: "hidden",
  },
  diffHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexWrap: "wrap",
  },
  diffTitle: {
    fontWeight: 600,
    fontSize: 14,
    flex: 1,
  },
  diffBody: {
    flex: 1,
    overflowY: "auto",
    fontFamily: "ui-monospace, SFMono-Regular, Consolas, monospace",
    fontSize: 12.5,
    lineHeight: 1.6,
    padding: theme.spacing(1, 0),
    ...theme.scrollbarStyles,
  },
  line: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    padding: theme.spacing(0, 1.5),
  },
  lineAdded: {
    background: theme.palette.mode === "dark" ? "rgba(46,160,67,0.18)" : "rgba(46,160,67,0.12)",
  },
  lineRemoved: {
    background: theme.palette.mode === "dark" ? "rgba(248,81,73,0.18)" : "rgba(248,81,73,0.12)",
    textDecoration: "line-through",
    opacity: 0.8,
  },
  legend: {
    fontSize: 11.5,
    color: theme.palette.text.secondary,
  },
  empty: {
    margin: "auto",
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
}));

const diffLines = (before, after) => {
  const a = String(before || "").split("\n");
  const b = String(after || "").split("\n");
  const table = Array.from({ length: a.length + 1 }, () => new Uint16Array(b.length + 1));
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      table[i][j] = a[i] === b[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }
  const result = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      result.push({ type: "same", text: a[i] });
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      result.push({ type: "removed", text: a[i] });
      i += 1;
    } else {
      result.push({ type: "added", text: b[j] });
      j += 1;
    }
  }
  while (i < a.length) result.push({ type: "removed", text: a[i++] });
  while (j < b.length) result.push({ type: "added", text: b[j++] });
  return result;
};

const formatDate = (value) =>
  new Date(value).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });

const PromptVersionHistory = ({ open, onClose, promptId, currentText, onRestored }) => {
  const { classes } = useStyles();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedText, setSelectedText] = useState("");
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    if (!open || !promptId) return;
    setLoading(true);
    setSelected(null);
    setSelectedText("");
    api
      .get(`/prompt/${promptId}/versions`)
      .then(({ data }) => {
        setVersions(data || []);
        const firstOld = (data || []).find((version) => !version.current);
        if (firstOld) setSelected(firstOld.version);
      })
      .catch(toastError)
      .finally(() => setLoading(false));
  }, [open, promptId]);

  useEffect(() => {
    if (!open || !promptId || !selected) return;
    api
      .get(`/prompt/${promptId}/versions/${selected}`)
      .then(({ data }) => setSelectedText(data?.prompt || ""))
      .catch(toastError);
  }, [open, promptId, selected]);

  const diff = useMemo(() => (selectedText ? diffLines(selectedText, currentText) : []), [selectedText, currentText]);
  const changes = diff.filter((line) => line.type !== "same").length;
  const selectedVersion = versions.find((version) => version.version === selected);

  const restore = async () => {
    if (!selected) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(i18n.t("promptVersions.confirmRestore", { version: selected }))) return;
    setRestoring(true);
    try {
      await api.post(`/prompt/${promptId}/versions/${selected}/restore`);
      toast.success(i18n.t("promptVersions.restored", { version: selected }));
      onRestored?.();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{i18n.t("promptVersions.title")}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <div className={classes.empty}>
            <CircularProgress size={22} />
          </div>
        ) : !versions.length ? (
          <Typography className={classes.empty}>{i18n.t("promptVersions.empty")}</Typography>
        ) : (
          <div className={classes.content}>
            <div className={classes.tableBox}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{i18n.t("promptVersions.columns.version")}</TableCell>
                    <TableCell>{i18n.t("promptVersions.columns.date")}</TableCell>
                    <TableCell>{i18n.t("promptVersions.columns.note")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {versions.map((version) => (
                    <TableRow
                      key={version.version}
                      hover
                      className={`${classes.row} ${selected === version.version ? classes.rowSelected : ""}`}
                      onClick={() => setSelected(version.version)}
                    >
                      <TableCell>
                        <strong>v{version.version}</strong>
                        {version.current && (
                          <Chip size="small" color="success" variant="outlined" label={i18n.t("promptVersions.current")} style={{ marginLeft: 6 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <div>{formatDate(version.createdAt)}</div>
                        <div className={classes.note}>{version.createdBy || "—"}</div>
                      </TableCell>
                      <TableCell className={classes.note}>{version.note || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className={classes.diffBox}>
              {selectedVersion ? (
                <>
                  <div className={classes.diffHeader}>
                    <Typography className={classes.diffTitle}>
                      {i18n.t("promptVersions.compare", { version: selectedVersion.version })}
                    </Typography>
                    <Typography className={classes.legend}>
                      {changes
                        ? i18n.t("promptVersions.changes", { count: changes })
                        : i18n.t("promptVersions.noChanges")}
                    </Typography>
                  </div>
                  <div className={classes.diffBody}>
                    {diff.map((line, index) => (
                      <div
                        key={index}
                        className={`${classes.line} ${
                          line.type === "added" ? classes.lineAdded : line.type === "removed" ? classes.lineRemoved : ""
                        }`}
                      >
                        {line.type === "added" ? "+ " : line.type === "removed" ? "- " : "  "}
                        {line.text || " "}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <Typography className={classes.empty}>{i18n.t("promptVersions.pick")}</Typography>
              )}
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Typography className={classes.legend} style={{ marginRight: "auto", paddingLeft: 8 }}>
          {i18n.t("promptVersions.legend")}
        </Typography>
        <Button onClick={onClose}>{i18n.t("promptVersions.close")}</Button>
        <Button
          variant="contained"
          onClick={restore}
          disabled={!selectedVersion || selectedVersion.current || restoring}
        >
          {i18n.t("promptVersions.restore", { version: selected || "" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromptVersionHistory;
